// Pure empty-creds-probe phase machine for the RTSP Assisted Setup wizard.
//
// The bug this solves: the credentials section is always optional, and if the
// user types credentials against an RTSP server that has NO auth, the stream
// still reaches Playing — so `AUTHENTICATION_STATUS_VALIDATED` means only
// "the pipeline reached Playing", NOT "the server actually checked the creds".
// The UI cannot otherwise tell "creds the server validated" from "creds the
// server ignored".
//
// The discriminator (replicated locally from the one-click empty-creds probe):
// probe FIRST with EMPTY credentials. If the anonymous connection is accepted,
// the server ignores creds → warn the user (their creds were never validated)
// but let them continue. If the anonymous connection is rejected/ambiguous,
// the server DOES enforce auth → probe again with the REAL creds and surface a
// normal validated / invalid verdict.
//
// This module is PURE: it holds no React state and performs no I/O. The wiring
// in rtsp.tsx owns the side effects (writing candidate.video / candidate.control
// to spin up the preview transients, timers) and feeds the transient verdicts
// back in as events. Keeping the decision logic here makes every branch of the
// (RTSP bucket × HTTP bucket) truth table unit-testable without a live encoder.

import { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';

/** Phase of the empty-creds-probe discriminator. */
export enum Phase {
  /** Not probing (no creds entered, prefilled, or reset by an input change). */
  IDLE = 'IDLE',
  /** Probing with EMPTY credentials to test whether the server enforces auth. */
  PROBE_EMPTY = 'PROBE_EMPTY',
  /** Empty probe showed the server enforces auth — re-probing with REAL creds. */
  PROBE_REAL = 'PROBE_REAL',
  /** Terminal: see `outcome`. */
  DONE = 'DONE'
}

/**
 * Terminal outcome of the machine.
 *   ok    — the entered credentials were validated (video opened on real creds).
 *   warn  — the device accepted the connection WITHOUT the credentials, so they
 *           could not be validated; the user may continue anyway.
 *   error — the server enforces auth and rejected the real credentials.
 */
export type Outcome = 'ok' | 'warn' | 'error';

/**
 * Which warning copy applies. Every reason means no plane confirmed the creds;
 * the copy says which plane could not.
 *   empty-accepted      — Phase 1 empty probe was accepted (the stream needs no
 *                         login, and control, if any, took empty creds).
 *   control-unconfirmed — Phase 2: the stream needs no login and camera control
 *                         did not confirm the real creds.
 *   unvalidated         — Phase 2: the empty probe got no stream verdict and
 *                         control could not validate the real creds.
 */
export type WarnReason = 'empty-accepted' | 'control-unconfirmed' | 'unvalidated';

/**
 * RTSP verdict from the EMPTY-creds probe (video plane).
 *   RTSP_NO_AUTH       — anonymous video opened (VALIDATED) → server ignores creds.
 *   RTSP_AUTH_REQUIRED — anonymous video rejected (INVALID) → server enforces auth.
 *   RTSP_AMBIGUOUS     — no verdict, but the connection failed / timed out. The
 *                        RTSP driver NEVER emits ERROR, so ambiguity is only ever
 *                        "no verdict + connectionFailed/timeout".
 */
export type RtspVerdict = 'RTSP_NO_AUTH' | 'RTSP_AUTH_REQUIRED' | 'RTSP_AMBIGUOUS';

/**
 * HTTP-control verdict from the EMPTY-creds probe. Only meaningful when a real
 * model is selected (an HTTP control module exists to probe); otherwise HTTP_NA.
 *   HTTP_AUTH_REQUIRED — control rejected empty creds (INVALID).
 *   HTTP_ACCEPTS_EMPTY — control accepted / cannot-enforce (VALIDATED|UNSUPPORTED).
 *   HTTP_UNKNOWN       — control errored (ERROR), or timed out with no verdict.
 *   HTTP_NA            — no model selected, so there is no control plane to probe.
 */
export type HttpVerdict = 'HTTP_AUTH_REQUIRED' | 'HTTP_ACCEPTS_EMPTY' | 'HTTP_UNKNOWN' | 'HTTP_NA';

/** Decision after Phase 1: re-probe with real creds, or warn the user. */
export type Phase1Decision = 'PROBE_REAL' | 'WARNING';

/** Auxiliary RTSP signals the video auth status alone does not carry. */
export interface RtspSignals {
  /** The sticky connection-failed latch fired (the transport gave up). */
  connectionFailed: boolean;
  /** The joint 15s phase timeout elapsed with no terminal verdict. */
  timedOut: boolean;
}

/** Auxiliary HTTP signals. */
export interface HttpSignals {
  /**
   * A known brand + real model is selected (so an HTTP control module exists).
   * Brand="Other" auto-seeds a generic modelId, so a bare modelId is NOT enough
   * — the caller must pass the real "known brand + a model" predicate. A known
   * brand + Model="Other" (generic template) still counts (it may carry a real
   * `http_module_login`); only the catch-all Brand="Other" is excluded.
   */
  modelSelected: boolean;
  /** The joint 15s phase timeout elapsed with no terminal verdict. */
  timedOut: boolean;
  /**
   * A SYNCHRONOUS control-preview error matched an auth rejection (400/401/403).
   * The mediator reports these via `previews.control.error` INSTEAD of a
   * transient `controlAuthenticationStatus`, so they must be treated as an auth
   * reject here. Without this a definite rejection has no terminal status and
   * degrades to HTTP_UNKNOWN after the 15s wait. Defaults false.
   */
  errorAuthFail?: boolean;
}

/**
 * Whether a preview device's URX authority carries userinfo (`user:pass@` or
 * `user@`). The empty-creds probe writes a credential-FREE candidate, so its
 * transient URX carries NO userinfo; the real-creds re-probe writes creds, so
 * its URX DOES. Correlating the read verdict to the URX is how each phase
 * accepts ONLY its own transient's verdict and never a stale one left over from
 * the previous phase's async teardown.
 *
 * The `/` is escaped (`\/`) so the vendored esbuild test transformer does not
 * mis-tokenize the regex terminator.
 */
export const urxHasCredentials = (urx: T_Maybe<string>): boolean =>
  // eslint-disable-next-line no-useless-escape
  /:\/\/[^\/@\s]+@/.test(urx ?? '');

/**
 * Classify the EMPTY-creds RTSP (video) probe.
 *
 * @returns the RTSP verdict, or `undefined` while still pending (no verdict and
 *   no failure/timeout yet — the caller keeps waiting).
 */
export const classifyRtspEmpty = (
  videoAuth: T_Maybe<AuthenticationStatus>,
  { connectionFailed, timedOut }: RtspSignals
): T_Maybe<RtspVerdict> => {
  // Video opened anonymously → the server did not check credentials.
  if (videoAuth === AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED) {
    return 'RTSP_NO_AUTH';
  }
  // Anonymous connection deterministically rejected → the server enforces auth.
  if (videoAuth === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID) {
    return 'RTSP_AUTH_REQUIRED';
  }
  // No verdict, but the transport gave up → ambiguous (the RTSP driver never
  // emits ERROR, so this is the only ambiguity source).
  if (connectionFailed || timedOut) {
    return 'RTSP_AMBIGUOUS';
  }
  // VALIDATING / UNKNOWN / undefined and no failure yet → still pending.
  return undefined;
};

/**
 * Classify the EMPTY-creds HTTP (control) probe.
 *
 * @returns the HTTP verdict, or `undefined` while still pending.
 */
export const classifyHttpEmpty = (
  controlAuth: T_Maybe<AuthenticationStatus>,
  { modelSelected, timedOut, errorAuthFail = false }: HttpSignals
): T_Maybe<HttpVerdict> => {
  // No model → no control plane to probe; this aspect is not applicable.
  if (!modelSelected) {
    return 'HTTP_NA';
  }
  // A synchronous 400/401/403 preview error is a definite auth rejection —
  // treat it exactly like an INVALID status, so we don't wait the full 15s and
  // degrade a real rejection to HTTP_UNKNOWN.
  if (errorAuthFail) {
    return 'HTTP_AUTH_REQUIRED';
  }
  switch (controlAuth) {
    case AuthenticationStatus.AUTHENTICATION_STATUS_INVALID:
      return 'HTTP_AUTH_REQUIRED';
    case AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED:
    case AuthenticationStatus.AUTHENTICATION_STATUS_UNSUPPORTED:
      return 'HTTP_ACCEPTS_EMPTY';
    case AuthenticationStatus.AUTHENTICATION_STATUS_ERROR:
      return 'HTTP_UNKNOWN';
    default:
      // UNKNOWN / VALIDATING / undefined: pending, unless the timeout forces us
      // to treat "could not get a verdict in time" as HTTP_UNKNOWN.
      return timedOut ? 'HTTP_UNKNOWN' : undefined;
  }
};

/**
 * The Phase-1 decision truth table. Re-probe with real creds when ANY aspect
 * signalled that credentials matter (RTSP auth required, RTSP ambiguous, HTTP
 * auth required, or HTTP unknown). Only warn when RTSP opened anonymously AND
 * HTTP accepted the empty creds (or there is no HTTP plane) — i.e. nobody
 * rejected the empty credentials.
 */
export const decidePhase1 = (rtsp: RtspVerdict, http: HttpVerdict): Phase1Decision => {
  if (
    rtsp === 'RTSP_AUTH_REQUIRED' ||
    rtsp === 'RTSP_AMBIGUOUS' ||
    http === 'HTTP_AUTH_REQUIRED' ||
    http === 'HTTP_UNKNOWN'
  ) {
    return 'PROBE_REAL';
  }
  // Only reachable when rtsp === RTSP_NO_AUTH and http ∈ {ACCEPTS_EMPTY, NA}.
  return 'WARNING';
};

/**
 * The Phase-2 (real-creds) VIDEO outcome. Continue gating is driven by the
 * video plane ONLY: an opened video is a success even if the control
 * plane later rejects the creds (that just shows an advisory error).
 *
 * @returns 'ok' when video opened, 'error' when video was rejected, or
 *   `undefined` while still pending.
 */
export const decidePhase2 = (
  videoAuth: T_Maybe<AuthenticationStatus>,
  timedOut = false
): T_Maybe<Outcome> => {
  if (videoAuth === AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED) {
    return 'ok';
  }
  if (videoAuth === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID) {
    return 'error';
  }
  // A real-creds video verdict that never lands (unreachable host, no-verdict
  // video) must NOT leave the machine stuck in PROBE_REAL forever. When the
  // joint phase timeout elapses with no terminal video verdict, resolve to
  // `error` so DONE runs (cleanup + control clear) instead of hanging.
  if (timedOut) {
    return 'error';
  }
  return undefined;
};

/** Whether Phase 2 must also re-probe the HTTP control plane with real creds. */
export const phase2NeedsHttp = (http1: HttpVerdict): boolean =>
  http1 === 'HTTP_AUTH_REQUIRED' || http1 === 'HTTP_UNKNOWN';

/**
 * The warning for a Phase 2 that opened video with no control rejection, keyed
 * on which plane actually checked the real creds:
 *   - the stream refused the empty probe (RTSP_AUTH_REQUIRED) and then played,
 *     or control VALIDATED them → validated, no warning;
 *   - the stream needs no login (RTSP_NO_AUTH), so only control could check them
 *     and it did not → 'control-unconfirmed';
 *   - the empty probe got no stream verdict and control was unknowable →
 *     'unvalidated'.
 */
export const phase2WarnReason = (
  rtsp1: T_Maybe<RtspVerdict>,
  http1: T_Maybe<HttpVerdict>,
  controlAuth: T_Maybe<AuthenticationStatus>
): T_Maybe<WarnReason> => {
  if (
    rtsp1 === 'RTSP_AUTH_REQUIRED' ||
    controlAuth === AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED
  ) {
    return undefined;
  }
  if (rtsp1 === 'RTSP_NO_AUTH') {
    return 'control-unconfirmed';
  }
  return http1 === 'HTTP_UNKNOWN' ? 'unvalidated' : undefined;
};

/** Phase-2 control-plane rejection (advisory — does NOT gate Continue). */
export const phase2ControlError = (controlAuth: T_Maybe<AuthenticationStatus>): boolean =>
  controlAuth === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID;

/**
 * Why a terminal `outcome: 'error'` was reached.
 *   rejected — the server enforced auth and rejected the real creds (INVALID);
 *              the standard "Authentication failed" auth Callout covers this.
 *   timeout  — no real-creds video verdict arrived before the phase timeout, so
 *              the wizard could not verify the connection; it needs its OWN
 *              user-visible message (an INVALID status Callout would not show).
 */
export type ErrorReason = 'rejected' | 'timeout';

/** Full machine state. */
export interface ProbeState {
  phase: Phase;
  /**
   * Monotonic phase generation. Incremented on every START / RESET and on the
   * EMPTY_VERDICT→PROBE_REAL transition, so each probe phase has a unique id. The
   * wiring arms its 15s phase timeout against the generation that was current
   * when the timer started; a timeout whose generation no longer matches
   * `generation` belongs to a phase the machine has already left and is ignored.
   * Without it a Phase-1 timeout's stale `timedOut=true` terminated Phase 2 the
   * instant it began (real creds never validated).
   */
  generation: number;
  /** Retained Phase-1 RTSP verdict (drives Phase-2 needs + diagnostics). */
  rtsp?: RtspVerdict;
  /** Retained Phase-1 HTTP verdict. */
  http?: HttpVerdict;
  /** Terminal outcome (set only when `phase === DONE`). */
  outcome?: Outcome;
  /** Why `outcome === 'error'` (set only alongside a DONE error outcome). */
  errorReason?: ErrorReason;
  /** Which warning copy applies (set only when a warning is shown). */
  warnReason?: WarnReason;
  /** Phase-2 control INVALID — advisory error, does not gate Continue. */
  controlError?: boolean;
}

/** The machine's initial / reset state. */
export const initialProbeState: ProbeState = { phase: Phase.IDLE, generation: 0 };

/** Events the wiring dispatches into the reducer. */
export type ProbeEvent =
  | { type: 'RESET' }
  | { type: 'START' }
  | { type: 'EMPTY_VERDICT'; rtsp: RtspVerdict; http: HttpVerdict }
  | {
      type: 'REAL_VERDICT';
      video: T_Maybe<AuthenticationStatus>;
      control?: T_Maybe<AuthenticationStatus>;
      /** A synchronous control 400/401/403 preview error (auth reject). */
      controlErrorAuthFail?: boolean;
      /** The joint phase timeout elapsed (force a terminal outcome). */
      timedOut?: boolean;
    };

/**
 * The phase-machine reducer: IDLE → PROBE_EMPTY → PROBE_REAL → DONE(ok|warn|error).
 *
 * Every transition is guarded on the current phase so a stale, late-arriving
 * event (e.g. a verdict for a probe the user already superseded) is ignored
 * rather than corrupting the phase — the wiring resets to IDLE whenever the
 * effective URL changes, and only the phase that owns an event acts on it.
 */
export const probeReducer = (state: ProbeState, event: ProbeEvent): ProbeState => {
  switch (event.type) {
    case 'RESET':
      // Bump the generation past the current one (never back to 0) so any
      // in-flight phase timer whose generation is captured is invalidated, and
      // a fresh probe can never collide with a stale timeout.
      return { phase: Phase.IDLE, generation: state.generation + 1 };

    case 'START':
      // Begin the empty-creds probe. Only meaningful from IDLE; a START while
      // already probing/done is a no-op (the wiring never double-starts). A new
      // generation scopes Phase 1's timeout to this phase.
      return state.phase === Phase.IDLE
        ? { phase: Phase.PROBE_EMPTY, generation: state.generation + 1 }
        : state;

    case 'EMPTY_VERDICT': {
      if (state.phase !== Phase.PROBE_EMPTY) {
        return state;
      }
      const decision = decidePhase1(event.rtsp, event.http);
      if (decision === 'WARNING') {
        // Server ignored the empty creds → warn, but let the user continue.
        return {
          phase: Phase.DONE,
          generation: state.generation,
          outcome: 'warn',
          warnReason: 'empty-accepted',
          rtsp: event.rtsp,
          http: event.http
        };
      }
      // Server enforces auth → re-probe with the real credentials. A new
      // generation gives Phase 2 its OWN timeout, so a Phase-1 timeout that has
      // already fired for the previous generation can never terminate it.
      return {
        phase: Phase.PROBE_REAL,
        generation: state.generation + 1,
        rtsp: event.rtsp,
        http: event.http
      };
    }

    case 'REAL_VERDICT': {
      if (state.phase !== Phase.PROBE_REAL) {
        return state;
      }
      const outcome = decidePhase2(event.video, event.timedOut ?? false);
      if (outcome === undefined) {
        // Real-creds video verdict not in yet — keep waiting.
        return state;
      }
      // A control-plane rejection is either an INVALID status OR a synchronous
      // 400/401/403 preview error. Either way it is advisory only.
      const controlError =
        phase2ControlError(event.control) || (event.controlErrorAuthFail ?? false);
      // Video opened but no plane confirmed the creds → still a success for
      // gating, with a warning naming what could not check them. A DEFINITE
      // control rejection (controlError) yields only the control-auth advisory —
      // a second, softer warning would contradict it.
      const warnReason: T_Maybe<WarnReason> =
        outcome === 'ok' && !controlError
          ? phase2WarnReason(state.rtsp, state.http, event.control)
          : undefined;
      // Distinguish a timeout error (no verdict) from a genuine reject (INVALID).
      // Only the timeout error needs its own user-visible message — an INVALID
      // already surfaces the standard "Authentication failed" auth Callout, so
      // double-messaging it would contradict.
      const errorReason: T_Maybe<ErrorReason> =
        outcome === 'error'
          ? event.video === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID
            ? 'rejected'
            : 'timeout'
          : undefined;
      // exactOptionalPropertyTypes: omit optional keys entirely when undefined
      // rather than assigning `key: undefined` (TS2375 rejects the latter against
      // the optional `warnReason?` / `errorReason?`).
      return {
        ...state,
        phase: Phase.DONE,
        outcome,
        ...(warnReason !== undefined ? { warnReason } : {}),
        ...(errorReason !== undefined ? { errorReason } : {}),
        controlError
      };
    }

    default:
      return state;
  }
};
